import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, { Navigation, Pagination, Thumbs } from 'swiper/core'
import { useTranslation } from 'next-i18next'
import type { Maybe, ProductVideo, ProductImage } from '@pylot-data/pylotschema'
import React, { ReactElement, useState, useEffect } from 'react'
import cn from 'classnames'
import s from './ScrollGallery.module.scss'
import swiperStyles from '@commonStyles/Swiper.module.scss'
import { ThumbsEvents, ThumbsOptions } from 'swiper/types'
import dynamic from 'next/dynamic'
import ImageSlide from '../util/ImageSlide'

const FullscreenGallery = dynamic(() => import('../FullscreenGallery'))
const VideoPlayer = dynamic(() => import('../VideoPlayer'))
SwiperCore.use([Navigation, Pagination, Thumbs])

type ProducGalleryType = {
    mediaGalleryEntries: Maybe<ProductVideo | ProductImage>[]
    zoom: boolean
    showFullscreen: boolean
    thumb?: boolean
    sliderProps?: {
        main?: {
            spaceBetween?: number
            navigation?: boolean
            pagination?:
                | boolean
                | {
                      clickable: boolean
                  }
        }
        thumbs?: {
            spaceBetween?: number
            slidesPerView?: number
        }
    }
    badgeLabel?: string
}

const defaultSliderProps = {
    main: {
        spaceBetween: 10,
        navigation: true,
        pagination: {
            clickable: true
        }
    },
    thumbs: {
        spaceBetween: 10,
        slidesPerView: 8
    }
}

const ScrollGallery = ({
    mediaGalleryEntries,
    zoom,
    thumb = false,
    sliderProps,
    badgeLabel
}: ProducGalleryType): ReactElement => {
    const { t } = useTranslation(['common'])

    const [isFullscreen, setFullscreen] = useState(false)
    const closeFullscreen = () => setFullscreen(false)

    const [sliderIndex, setSliderIndex] = useState(0)

    const openFullscreen = (i: number) => {
        setSliderIndex(i)
        setFullscreen(true)
    }

    const [thumbsSwiper, setThumbsSwiper] = useState<ThumbsEvents>()
    const syncThumbnail = (swiperCore: SwiperCore, delay: number) => {
        //Quick fix to swiper bug
        setTimeout(() => swiperCore.thumbs.update(false), delay)
    }

    const renderImages = (
        mediaGalleryData: Maybe<ProductVideo | ProductImage>[],
        productImageAlt: string,
        width = 1050,
        height = 1050,
        showFullscreenGallery = false,
        isThumbnail = false
    ) =>
        mediaGalleryData?.map((image, i) => (
            <div key={image?.url} className={s['scroll-image-container']}>
                <SwiperSlide
                    className={
                        image?.__typename === 'ProductVideo'
                            ? 'video-thumb'
                            : ''
                    }
                >
                    {image?.__typename === 'ProductVideo' ? (
                        <VideoPlayer
                            url={`${image.video_content?.video_url!}`}
                            index={i}
                            image={image?.url}
                            label={image?.label}
                            isThumbnail={isThumbnail}
                        />
                    ) : (
                        <ImageSlide
                            image={image}
                            productImageAlt={productImageAlt}
                            width={width}
                            height={height}
                            index={i}
                            zoom={zoom}
                            showFullscreen={showFullscreenGallery}
                            s={s}
                            openFullscreen={openFullscreen}
                        />
                    )}
                </SwiperSlide>
            </div>
        ))

    useEffect(() => {
        const html = document.getElementsByTagName('html')[0]
        html.classList.remove('zoom-open')
        if (isFullscreen) html.classList.add('zoom-open')
    }, [isFullscreen])

    return (
        <div className={s['pdp-wrapper']}>
            {badgeLabel && <span className={s['pdp-badge']}>{badgeLabel}</span>}
            <div className={cn('pdp-slider', swiperStyles.root)}>
                {isFullscreen && (
                    <FullscreenGallery
                        closeFullscreen={closeFullscreen}
                        mediaGalleryEntries={mediaGalleryEntries}
                        sliderIndex={sliderIndex}
                    />
                )}
                <div className={cn(s['pdp-slider-main'])}>
                    <Swiper
                        {...{
                            ...defaultSliderProps.main,
                            ...sliderProps?.main
                        }}
                        thumbs={{ swiper: thumbsSwiper } as ThumbsOptions}
                        onSwiper={(swiperCore: SwiperCore) =>
                            syncThumbnail(swiperCore, 300)
                        }
                        onSlideChange={(swiperCore: SwiperCore) =>
                            syncThumbnail(swiperCore, 100)
                        }
                    >
                        {renderImages(
                            mediaGalleryEntries,
                            t('Product Image'),
                            1050,
                            1050,
                            true
                        )}
                    </Swiper>
                    {thumb && (
                        <div className="thumbnails">
                            <Swiper
                                onSwiper={setThumbsSwiper}
                                {...{
                                    ...defaultSliderProps.thumbs,
                                    ...sliderProps?.thumbs
                                }}
                                freeMode
                                watchSlidesProgress
                            >
                                {renderImages(
                                    mediaGalleryEntries,
                                    t('Product Image'),
                                    100,
                                    100,
                                    false,
                                    true
                                )}
                            </Swiper>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default ScrollGallery
